import { MoreOutlined } from '@ant-design/icons';
import { WORKFLOW_DATE_FORMATS } from '@bmo/form-core';
import { Button, Checkbox, Form, Input, Select, Switch, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  type TargetPlatform,
  type WorkflowDateFormat,
  type WorkflowReviewLayout,
  type WorkflowSummary,
  type WorkflowTemplate,
  type WorkflowType,
  type WorkflowVariable,
  workflowsApi,
} from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { BmoRadioGroup } from '@/components/BmoRadioGroup';
import { DATE_INPUT_FORMAT } from '@/form-renderer/fieldValidation';
import { formatDatetime } from '@/lib/formatDatetime';
import { message } from '@/lib/message';

import { deriveAppNameBase } from './deriveAppNameBase';
import { extractApiError } from './statusUtils';
import { WorkflowVariablesEditor } from './WorkflowVariablesEditor';

type FormValues = {
  nameEn: string;
  nameFr: string;
  descriptionEn?: string;
  descriptionFr?: string;
  appName: string;
  appNameFr?: string;
  type: WorkflowType;
  platform: TargetPlatform;
  includedDealerCodes: string[];
  excludedDealerCodes: string[];
  subsequentApplication: boolean;
  packageFilenameTemplate?: string;
  dateFormat: WorkflowDateFormat;
  /** '' = the original per-section review; the radio needs a concrete value for "unset". */
  reviewLayout: WorkflowReviewLayout | '';
  formatPhoneNumbers: boolean;
  /** Workflow variables, authored in the Advanced panel. Rows are partial while being typed —
   *  the server drops entirely-blank ones, so an untouched "+" row is not an error. */
  variables: WorkflowVariable[];
};

/**
 * Trim the variable rows and drop the entirely-blank ones, so an untouched row the originator
 * added and left behind never reaches the wire.
 *
 * Mirrors `WorkflowVariable.NormalizeAndValidate`, including the part that matters: a row with
 * a NAME but no value is kept. An empty string is a legitimate value to pin a field to, and
 * discarding it here would read as the variable silently refusing to save.
 */
function cleanVariables(rows: WorkflowVariable[] | undefined): WorkflowVariable[] {
  return (rows ?? [])
    .map((row) => ({ key: (row?.key ?? '').trim(), value: (row?.value ?? '').trim() }))
    .filter((row) => row.key.length > 0 || row.value.length > 0);
}

/** Mirrors `DealerCodeRules.Pattern` on the server: digits only, at most 6 of them. */
const DEALER_CODE_PATTERN = /^[0-9]{1,6}$/;

/** Mirrors `DealerCodeRules.MaxCodes` — counted per list, not across both. */
const DEALER_CODE_MAX_COUNT = 100;

/**
 * Mirrors `WorkflowTemplate.MaxTitleLength` on the server. The titles go to BMO as
 * `appNameEn` / `appNameFr`, which their publish contract leaves unbounded — the domain
 * is the enforcing side, this just stops the typing before the round trip.
 */
const TITLE_MAX_LENGTH = 150;

/** Mirrors `WorkflowFormSettings.MaxPackageFilenameTemplateLength` on the server. */
const PACKAGE_FILENAME_TEMPLATE_MAX_LENGTH = 200;

type Props = {
  open: boolean;
  onClose: () => void;
  /** When set, the modal switches to edit mode (prefilled, save calls update). */
  /** Summary is enough — the modal only edits card metadata (names, appName,
   *  type, platform, dealer codes). Steps are edited on the details page. */
  editing?: WorkflowSummary | null;
  /**
   * Called after a successful create or save. Receives the updated/created template
   * so the parent can patch its local state instead of re-fetching from the eventually-
   * consistent byOwner GSI (which can briefly return stale rows).
   */
  onSaved?: (saved: WorkflowTemplate) => void;
};

/**
 * Workflow create / edit dialog. English title + application name + assignment
 * type are mandatory; French title + both descriptions are optional (ADR-0016).
 * App name is auto-derived from the English title and immutable after creation; its
 * optional French counterpart is plain free text and stays editable.
 */
export function CreateWorkflowModal({ open, onClose, editing, onSaved }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const isEdit = !!editing;

  // Timestamp captured once when the modal opens in create mode so it stays
  // stable while the user edits the title.
  const openTimestamp = useRef('');

  // Auto-derive appName from the English title in create mode. `deriveAppNameBase`
  // lowercases + strips non-slug chars so the composed name satisfies the domain
  // regex `^[a-z][a-z0-9-]{2,63}$` — previously an uppercase title like
  // "BMO RDSP" produced "BMO-RDSP-<stamp>" and the Create call failed with
  // "Application Name (Smartform Name) is Invalid".
  const nameEn = Form.useWatch('nameEn', form);
  const subsequentApplication = Form.useWatch('subsequentApplication', form);
  useEffect(() => {
    if (!isEdit) {
      const base = deriveAppNameBase(nameEn ?? '');
      form.setFieldValue('appName', base ? `${base}-${openTimestamp.current}` : '');
    }
  }, [nameEn, isEdit, form]);

  // Prefill when opening in edit mode; capture timestamp when opening in create mode.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        nameEn: editing.nameEn,
        nameFr: editing.nameFr,
        descriptionEn: editing.descriptionEn,
        descriptionFr: editing.descriptionFr,
        appName: editing.appName,
        appNameFr: editing.appNameFr ?? '',
        type: editing.type,
        // Legacy workflows created before the field existed have no platform — default to APEX.
        platform: editing.platform ?? 'APEX',
        includedDealerCodes: editing.includedDealerCodes ?? [],
        excludedDealerCodes: editing.excludedDealerCodes ?? [],
        subsequentApplication: editing.subsequentApplication ?? false,
        packageFilenameTemplate: editing.packageFilenameTemplate ?? '',
        // Unset has always meant DD/MM/YYYY, so that is what the radio shows.
        dateFormat: editing.dateFormat ?? DATE_INPUT_FORMAT,
        reviewLayout: editing.reviewLayout ?? '',
        formatPhoneNumbers: editing.formatPhoneNumbers === true,
        // Copied, not aliased: Form.List mutates the array it is handed, and `editing` is the
        // list row the dashboard still renders from.
        variables: (editing.variables ?? []).map((v) => ({ ...v })),
      });
    } else {
      openTimestamp.current = formatDatetime(new Date());
      form.resetFields();
    }
  }, [open, editing, form]);

  const close = () => {
    form.resetFields();
    onClose();
  };

  const handleSave = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      let saved: WorkflowTemplate;
      if (editing) {
        // App name is immutable; carry the existing order through. The FRENCH app name is
        // not — it is a display label, so it stays editable after creation (which is also the
        // only way a workflow that predates the field can ever get one).
        saved = await workflowsApi.update(editing.id, {
          nameEn: values.nameEn,
          nameFr: values.nameFr,
          // Whole-state on the server, like packageFilenameTemplate below: blank clears it.
          appNameFr: values.appNameFr ?? '',
          descriptionEn: values.descriptionEn,
          descriptionFr: values.descriptionFr,
          type: values.type,
          platform: values.platform,
          order: editing.order,
          includedDealerCodes: values.includedDealerCodes,
          excludedDealerCodes: values.excludedDealerCodes,
          subsequentApplication: values.subsequentApplication,
          // Whole-state on the server: blank clears it.
          packageFilenameTemplate: values.packageFilenameTemplate || null,
          dateFormat: values.dateFormat,
          // Whole-state too: sending them back is what keeps an edit from switching them off.
          reviewLayout: values.reviewLayout || null,
          formatPhoneNumbers: values.formatPhoneNumbers || null,
          // Whole-state as well — omitting the key would clear every variable on the workflow,
          // and because variables are live that loss would reach advisors immediately.
          variables: cleanVariables(values.variables),
        });
        message.success(t('workflow.form.saved'));
      } else {
        saved = await workflowsApi.create({
          order: 0,
          ...values,
          packageFilenameTemplate: values.packageFilenameTemplate || null,
          reviewLayout: values.reviewLayout || null,
          formatPhoneNumbers: values.formatPhoneNumbers || null,
          variables: cleanVariables(values.variables),
        });
        message.success(t('workflow.form.created'));
      }
      form.resetFields();
      onClose();
      onSaved?.(saved);
      if (!editing) {
        navigate(`/originator/workflows/${saved.id}`);
      }
    } catch (err) {
      const e = extractApiError(err);
      if (e.details?.length) {
        form.setFields(
          e.details
            .filter((d) => d.field)
            .map((d) => ({ name: d.field as keyof FormValues, errors: [d.message] })),
        );
      } else {
        message.error(e.message ?? t('workflow.form.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <BmoModal
        open={open}
        onCancel={close}
        width={640}
        title={isEdit ? t('workflow.form.editTitle') : t('workflow.form.modalTitle')}
        headerExtra={
          <Tooltip title={t('workflow.form.advancedTooltip')}>
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined style={{ color: '#fff' }} />}
              aria-label={t('workflow.form.advancedTooltip')}
              onClick={() => setAdvancedOpen(true)}
            />
          </Tooltip>
        }
        footer={[
          <Button key="cancel" onClick={close}>
            {t('common.cancel')}
          </Button>,
          <Button key="save" type="primary" loading={submitting} onClick={handleSave}>
            {isEdit ? t('workflow.form.save') : t('common.create')}
          </Button>,
        ]}
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          initialValues={{
            type: 'Both',
            includedDealerCodes: [],
            excludedDealerCodes: [],
            subsequentApplication: false,
            dateFormat: DATE_INPUT_FORMAT,
            reviewLayout: '',
            formatPhoneNumbers: false,
            variables: [],
          }}
        >
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="nameEn"
              label={t('workflow.fields.titleEn')}
              rules={[{ required: true, message: t('workflow.form.titleEnRequired') }]}
              style={{ flex: 1 }}
            >
              <Input
                maxLength={TITLE_MAX_LENGTH}
                placeholder={t('workflow.form.titleEnPlaceholder')}
              />
            </Form.Item>
            <Form.Item
              name="nameFr"
              label={t('workflow.fields.titleFr')}
              rules={[{ required: true, message: t('workflow.form.titleFrRequired') }]}
              style={{ flex: 1 }}
            >
              <Input
                maxLength={TITLE_MAX_LENGTH}
                placeholder={t('workflow.form.titleFrPlaceholder')}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="descriptionEn"
              label={t('workflow.fields.descEnOptional')}
              extra={t('workflow.form.descEnHint')}
              style={{ flex: 1 }}
            >
              <Input.TextArea
                rows={4}
                maxLength={500}
                style={{ resize: 'none' }}
                placeholder={t('workflow.form.descEnPlaceholder')}
              />
            </Form.Item>
            <Form.Item
              name="descriptionFr"
              label={t('workflow.fields.descFrOptional')}
              extra={t('workflow.form.descFrHint')}
              style={{ flex: 1 }}
            >
              <Input.TextArea
                rows={4}
                maxLength={500}
                style={{ resize: 'none' }}
                placeholder={t('workflow.form.descFrPlaceholder')}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="appName"
              label={t('workflow.fields.appNameSmartform')}
              extra={t('workflow.form.appNameHint')}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input disabled />
            </Form.Item>
            {/* Free text, no rules: the French app name is display-only, so none of the
                format / reserved-word / uniqueness constraints that make `appName` derived
                and disabled apply to it. Bounded by TITLE_MAX_LENGTH, which mirrors the
                server's one check on this field. */}
            <Form.Item
              name="appNameFr"
              label={t('workflow.fields.appNameFrOptional')}
              extra={t('workflow.form.appNameFrHint')}
              style={{ flex: 1, marginBottom: 0 }}
            >
              <Input
                maxLength={TITLE_MAX_LENGTH}
                placeholder={t('workflow.form.appNameFrPlaceholder')}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="type"
            label={t('workflow.fields.assignmentType')}
            rules={[{ required: true, message: t('workflow.form.required') }]}
          >
            <BmoRadioGroup<WorkflowType>
              options={[
                { label: t('workflow.types.internal'), value: 'InternalAdvisor' },
                { label: t('workflow.types.external'), value: 'ExternalAdvisor' },
                { label: t('workflow.types.both'), value: 'Both' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="platform"
            label={t('workflow.fields.platform')}
            rules={[{ required: true, message: t('workflow.form.required') }]}
          >
            <BmoRadioGroup<TargetPlatform>
              options={[
                { label: t('workflow.platforms.IFDS'), value: 'IFDS' },
                { label: t('workflow.platforms.APEX'), value: 'APEX' },
              ]}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="includedDealerCodes"
              label={t('workflow.fields.includedDealerCodes')}
              extra={t('workflow.form.includedDealerCodesHint')}
              rules={[
                {
                  validator: dealerCodeListValidator(
                    t('workflow.form.dealerCodeInvalid'),
                    t('workflow.form.dealerCodeTooMany', { max: DEALER_CODE_MAX_COUNT }),
                  ),
                },
              ]}
              style={{ flex: 1 }}
            >
              <Select
                mode="tags"
                open={false}
                tokenSeparators={[',']}
                placeholder={t('workflow.form.dealerCodePlaceholder')}
              />
            </Form.Item>
            <Form.Item
              name="excludedDealerCodes"
              label={t('workflow.fields.excludedDealerCodes')}
              extra={t('workflow.form.excludedDealerCodesHint')}
              rules={[
                {
                  validator: dealerCodeListValidator(
                    t('workflow.form.dealerCodeInvalid'),
                    t('workflow.form.dealerCodeTooMany', { max: DEALER_CODE_MAX_COUNT }),
                  ),
                },
              ]}
              style={{ flex: 1 }}
            >
              <Select
                mode="tags"
                open={false}
                tokenSeparators={[',']}
                placeholder={t('workflow.form.dealerCodePlaceholder')}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="packageFilenameTemplate"
            label={t('workflow.fields.packageFilenameTemplate')}
            extra={t('workflow.form.packageFilenameTemplateHint')}
          >
            <Input
              maxLength={PACKAGE_FILENAME_TEMPLATE_MAX_LENGTH}
              placeholder={t('workflow.form.packageFilenameTemplatePlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="dateFormat"
            label={t('workflow.fields.dateFormat')}
            extra={t('workflow.form.dateFormatHint')}
          >
            <BmoRadioGroup<WorkflowDateFormat>
              options={WORKFLOW_DATE_FORMATS.map((f) => ({ label: f, value: f }))}
            />
          </Form.Item>

          <Form.Item
            name="reviewLayout"
            label={t('workflow.fields.reviewLayout')}
            extra={t('workflow.form.reviewLayoutHint')}
          >
            <BmoRadioGroup<WorkflowReviewLayout | ''>
              options={[
                { label: t('workflow.form.reviewLayoutSections'), value: '' },
                { label: t('workflow.form.reviewLayoutSteps'), value: 'steps' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="formatPhoneNumbers"
            valuePropName="checked"
            extra={t('workflow.form.formatPhoneNumbersHint')}
          >
            <Checkbox>{t('workflow.fields.formatPhoneNumbers')}</Checkbox>
          </Form.Item>

          <Form.Item name="subsequentApplication" hidden valuePropName="checked">
            <Switch />
          </Form.Item>

          {/* Advanced settings. Rendered INSIDE <Form> on purpose: the variables editor is a
              Form.List, which needs the surrounding form context to bind to. AntD portals the
              modal out of this DOM position anyway, and React context flows through portals,
              so nesting costs nothing visually and is what makes the binding work.

              The subsequent-application switch keeps its imperative setFieldValue proxy — it
              predates this nesting and works either way. */}
          <BmoModal
            open={advancedOpen}
            onCancel={() => setAdvancedOpen(false)}
            width={560}
            title={t('workflow.form.advancedModalTitle')}
            footer={[
              <Button key="done" type="primary" onClick={() => setAdvancedOpen(false)}>
                {t('common.done')}
              </Button>,
            ]}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Switch
                  checked={!!subsequentApplication}
                  onChange={(checked) => form.setFieldValue('subsequentApplication', checked)}
                />
                <span>{t('workflow.form.subsequentApplication')}</span>
              </div>
              <WorkflowVariablesEditor />
            </div>
          </BmoModal>
        </Form>
      </BmoModal>
    </>
  );
}

/** Rejects any tag that isn't 1–6 digits, and any list over the count cap — the
 *  chiclet list accepts free-text tags (comma-paste splits via `tokenSeparators`),
 *  so neither format nor length is enforced until validation runs. */
function dealerCodeListValidator(message: string, tooManyMessage: string) {
  return (_: unknown, value?: string[]) => {
    if (value?.some((code) => !DEALER_CODE_PATTERN.test(code))) {
      return Promise.reject(new Error(message));
    }
    // De-duped first so a paste with repeats is judged on what the server will
    // actually persist — `DealerCodeRules.NormalizeAndValidate` counts the same way.
    if (value && new Set(value).size > DEALER_CODE_MAX_COUNT) {
      return Promise.reject(new Error(tooManyMessage));
    }
    return Promise.resolve();
  };
}
